import React, { useState, useEffect, useRef } from 'react';
import { SearchOrderGlobalBinded } from '../../models/Orders_v2';
import Checkbox from '../Forms/Checkbox';
import { numberToCurrencyString } from '../../services/formatting';
import Button from '../Forms/Button';
import { useMutation } from '@apollo/client';
import { graphqlSchema } from '../../services/graphql.schema';

import { Bill, BillConceptos, BillConceptTax } from '../../models/Factura';
import sharedToasterSubject from '../../services/shared.toasterSubject';
import { ProfileQL } from '../../models/Profile';
import GlobalSectionReportDownload from './Download.unbilledgroup.reporte';
import Pagination from '../Forms/Pagination';
import { ordersToConceptos } from '../../utils/global.ordersToConceptos';
import { clearGqlTypename } from '../../utils/formatting';
import SelectCard from '../Forms/Select.cardForm';
import InputCard from '../Forms/Input.cardForm';

interface GlobalGroupingGroup {
    currentUser: ProfileQL,
    lugarExpedicion: string;
    label: string;
    serie: string;
    folio: number;
    group: SearchOrderGlobalBinded[];
    toggleSelect: (orderId: string) => (value: boolean) => void;
    unSelectedDict: { [key: string]: boolean };
    propagateStampedGroup: (orderIds: string[], uuid: string, lastDate: number) => void;
}

const FORMA_DE_PAGO_OPTIONS = [
    { label: 'Débito (04)', value: '04' },
    { label: 'Crédito (28)', value: '28' },
    { label: 'Intermediarios (31)', value: '31' },
    { label: 'Por definir (99)', value: '99' },
]

const ENTRIES_PER_PAGE = 35;

function StampGlobalGroupingGroup({ currentUser, toggleSelect, unSelectedDict, label, serie, folio, group, lugarExpedicion, propagateStampedGroup }: GlobalGroupingGroup) {
    const [groupSerie, setGroupSerie] = useState(serie);
    const [groupFolio, setGroupFolio] = useState(folio);
    const [enabledOrders, setEnabledOrders] = useState<SearchOrderGlobalBinded[]>(group);
    const [tax, setTax] = useState(0);
    const [iepsTax, setIepsTax] = useState(0);

    const [subtotal, setSubtotal] = useState(0);
    const [animageGroup, setAnimateGroup] = useState(false);
    const [formaPagoAuto, setFormaPago] = useState('');
    const [formaPagoOverride, setFormaPagoOverride] = useState('');
    const [metodoPagoOverride] = useState<'PUE' | 'PPD'>('PUE');
    const [dateOverride, setDateOverride] = useState(0);

    const [cfdiConceptos, setCfdiConceptos] = useState<Bill['conceptos']>([]);

    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);

    // Submitting
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submmittedDate, setSubmittedDate] = useState(null);

    const [stampGlobal] = useMutation(graphqlSchema.FISCALPOP.CFDI.setGlobalBillToStamp, {
        onCompleted: ({ setGlobalBillToStamp }: { setGlobalBillToStamp: { UUID: string } }) => {
            console.log('GlobalStamp CFDI: ', setGlobalBillToStamp);
            // UseReducer to update object array
            setIsSubmitting(false);
            propagateStampedGroup(enabledOrders.map(o => o.order.orderId), setGlobalBillToStamp.UUID, submmittedDate);
            sharedToasterSubject.next({ type: 'confirm', message: 'Se emitió la factura global correctamente' });
        },
        onError: (e) => {
            console.error('Error stamping global: ', e);
            setIsSubmitting(false);
            if(e.message.includes('Failed to fetch')) {
                // IF message includes a FAILED TO FETCH this must be a "Payload too large". Apollo doesn't log http.status error
                sharedToasterSubject.next({ type: 'error', clickToClose: true, message: 'La factura a emitir es muy grande.\nIntenta agrupar por semana o día para reducir su tamaño' });
                return
            }
            console.log(`setBillToStamp e.graphQLErrors[0]: `, e.graphQLErrors[0]);
            const errMessage = e.graphQLErrors[0]?.message ||  'Error al emitir la factura global';
            sharedToasterSubject.next({ type: 'error', message: errMessage });
        }
    });

    useEffect(() => {

        const _enabledGroup = group.filter(sales => !unSelectedDict[sales.order.orderId]);
        setEnabledOrders(_enabledGroup);
        if (!animageGroup && !_enabledGroup.length) {
            setAnimateGroup(true);
        }
        const formaPago = '31';
        setFormaPago(formaPago);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [unSelectedDict, group]);

    useEffect(() => {
        const conceptos = ordersToConceptos(enabledOrders);
        setCfdiConceptos(conceptos);
        const calculations = conceptos.reduce((p, c) => {
            const hasIeps = c.impuestos.find(tax => tax.type === 'ieps');
            const hasIva = c.impuestos.find(tax => tax.type === 'iva');

            const subt = c.valorUnitario;
            const ieps = !hasIeps ? 0 : c.valorUnitario * hasIeps.tasa;
            const iva = (subt + ieps) * hasIva.tasa;
            p.subtotal += c.valorUnitario;
            p.ieps += ieps;
            p.iva += iva;
            return p
        }, { subtotal: 0, ieps: 0, iva: 0 });
        const { iva, ieps, subtotal } = calculations;
        setTax(Math.round(iva * 100) / 100);
        setIepsTax(Math.round(ieps * 100) / 100);
        setSubtotal(Math.round(subtotal * 100) / 100);

    }, [enabledOrders])




    const submitGroupStamp = () => {
        setIsSubmitting(true);
        const mesForGlobal = new Date(group[0].order.createdTime).getMonth() + 1;
        const yearForGlobal = new Date(group[0].order.createdTime).getFullYear();

        const cfdi: Bill = {
            serie: groupSerie,
            folio: `${groupFolio}`,
            formaPago: formaPagoOverride || formaPagoAuto,
            metodoPago: metodoPagoOverride || 'PUE',
            lugarExpedicion,
            tipoDeComprobante: 'I',
            global: {
                ano: `${yearForGlobal}`,
                periodo: '04',
                mes: `${mesForGlobal}`
            },
            receptor: {
                rfc: 'XAXX010101000',
                nombre: 'PUBLICO EN GENERAL',
                usoCFDI: 'S01',
                regimen: '616',
                zip: currentUser.fiscalpopProfile.lugarExpedicion
            },
            conceptos: cfdiConceptos,

        }
        if (dateOverride) {
            cfdi.fecha = (new Date(Date.now() - Math.floor(dateOverride * 24 * 60 * 60 * 1000))).toJSON();
        }

        const lastDate = enabledOrders.map((o) => new Date(o.order.createdTime).getTime()).sort((a, b) => b - a)[0];
        console.log('CFDI To STAMP: ', cfdi);
        setSubmittedDate(lastDate);

        stampGlobal({
            variables: {
                orderIds: enabledOrders.map(o => o.order.orderId),
                cfdi: clearGqlTypename(cfdi),
                lastDate
            }
        })
    }

    const avoidFolioToBeRepeated = (_folio: number) => {
        if (_folio < 1) {
            _folio = 1;
            setGroupFolio(_folio);
        } else {
            setGroupFolio(_folio);
        }
    }

    const dateOverrideOptions = [
        { label: 'Hoy', value: 0 },
        { label: 'Ayer', value: 1 },
        { label: 'Hace dos dias', value: 2 },
        { label: 'Hace tres dias', value: 2.9 },
    ];

    // OLD PROCESS TO FILTER
    const fpStatus = !!currentUser.fiscalpopProfileStatus ? currentUser.fiscalpopProfileStatus : null;
    const modulusMatch = !fpStatus ? null : fpStatus.modulusMatch;
    /*
    const filteredGroup = !search ? group : group.filter(g => `${g.order.AmazonOrderId}`.includes(search));
    const filteredPaginationGroup = !search ? enabledOrders : enabledOrders.filter(g => `${g.order.AmazonOrderId}`.includes(search));
    const conceptosDisplayed = filteredGroup.filter((o, i) => (i >= (ENTRIES_PER_PAGE * (page - 1))) && (i < (ENTRIES_PER_PAGE * (page))))
    */
    const filteredGroup = !search ? cfdiConceptos : cfdiConceptos.filter(g => `${g.noIdentificacion}`.includes(search));
    const filteredPaginationGroup = !search ? cfdiConceptos : cfdiConceptos.filter(g => `${g.noIdentificacion}`.includes(search));
    const conceptosDisplayed = filteredGroup.filter((o, i) => (i >= (ENTRIES_PER_PAGE * (page - 1))) && (i < (ENTRIES_PER_PAGE * (page))))

    return (
        <div className={`card billGrouping ${!enabledOrders.length ? 'empty' : (animageGroup ? 'full' : '')}`}>
            <div className="cardTitle">
                <h4>
                    {label}
                </h4>
            </div>
            <div className="groupContent">
                <div className="groupHeader">
                    <SelectCard options={dateOverrideOptions} value={dateOverride} label={'Fecha a emitir'} onChange={setDateOverride} />
                </div>
                <div className="groupHeader">
                    <InputCard type="text" value={groupSerie} onChange={setGroupSerie} label={"Serie"} placeholder="Serie de la factura" />
                    <InputCard type="number" value={groupFolio} onChange={avoidFolioToBeRepeated} label={"Folio"} placeholder="Folio de la factura" />
                </div>
                <div className="groupAutocomplete">
                    <SelectCard options={FORMA_DE_PAGO_OPTIONS} value={formaPagoOverride || formaPagoAuto} label={'Forma de pago'} onChange={setFormaPagoOverride} />
                </div>
                <div className='search'>
                    <InputCard label="Buscar número de orden" type="text" value={search} onChange={setSearch} placeholder={'Buscar ID'} />
                </div>
                <div className="groupConcepts">
                    <div className="orderConcepts header">
                        <div></div>
                        <p>
                            Producto
                        </p>
                        <p className="precio">
                            Precio unitario <br />
                            (x Cantidad)
                        </p>
                        <p className="mq1">
                            Clave (SAT)
                        </p>
                        <p className="mq2">
                            Impuestos
                        </p>
                    </div>
                    {
                        conceptosDisplayed.map((concepto, j) => (
                            <StampGlobalGroupingGroupConcepto chargeShipping={false} isHidden={unSelectedDict[concepto.noIdentificacion.split('.')[0]]} key={concepto.noIdentificacion} sales={concepto} toggleSelect={toggleSelect} />

                        ))
                    }
                    <Pagination onPage={setPage} pages={Math.ceil(filteredPaginationGroup.length / ENTRIES_PER_PAGE)} page={page} />
                </div>
                <div className="totals">
                    <div />
                    <div className="col">
                        <p className="label"><b>Subtotal:</b></p>
                        <p>$ {numberToCurrencyString(subtotal)}</p>
                        {
                            iepsTax ?
                                <>
                                    <p className="label"><b>IEPS:</b></p>
                                    <p>$ {numberToCurrencyString(iepsTax)}</p>
                                </>
                                : ''
                        }
                        <p className="label"><b>IVA:</b></p>
                        <p>$ {numberToCurrencyString(tax)}</p>
                        <p className="label"><b>Total:</b></p>
                        <p>$ {numberToCurrencyString(subtotal + tax + (iepsTax || 0))}</p>
                    </div>
                </div>
                <div className="stampAction">
                    <GlobalSectionReportDownload billList={enabledOrders} label={label} chargeShipping={false} />

                    <Button primary={true} handleClick={submitGroupStamp} disabled={isSubmitting || !modulusMatch}>
                        <div className='iconned'>
                            {isSubmitting ? <span>Facturando...</span> : <span>Facturar Grupo</span>}
                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="20" viewBox="0 0 18 20" fill="none">
                                <path d="M15 19C15.5304 19 16.0391 18.8104 16.4142 18.4728C16.7893 18.1352 17 17.6774 17 17.2L17 2.8C17 2.32261 16.7893 1.86478 16.4142 1.52721C16.0391 1.18965 15.5304 1 15 1L3 1.00001C2.46957 1.00001 1.96086 1.18965 1.58579 1.52721C1.21072 1.86478 1 2.32262 1 2.8" stroke="#A4A5B2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M1.58579 1.52721C1.96086 1.18964 2.46957 1 3 1H14.9093C15.2317 1 15.5493 1.07764 15.8354 1.22634C16.2116 1.42189 16.5162 1.73148 16.7057 2.11077L16.7536 2.20668C16.9156 2.53111 17 2.88879 17 3.25143V6.4V17.2C17 17.6774 16.7893 18.1352 16.4142 18.4728C16.0391 18.8104 15.5304 19 15 19H3C2.46957 19 1.96086 18.8104 1.58579 18.4728C1.21071 18.1352 1 17.6774 1 17.2V2.8C1 2.32261 1.21071 1.86477 1.58579 1.52721Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M5 10.9L13 10.9" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M5 8.19995L13 8.19995" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M5 5.5L13 5.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M5 13.6L6 13.6L7 13.6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </div>
                    </Button>
                </div>
            </div>
        </div>
    )
}

interface GlobalGroupingGroupConcept {
    sales: BillConceptos;
    toggleSelect: (orderId: string) => (value: boolean) => void;
    isHidden: boolean;
    chargeShipping: boolean;
}


function StampGlobalGroupingGroupConcepto({ isHidden, sales, toggleSelect, chargeShipping }: GlobalGroupingGroupConcept) {
    const hideAnimationRef = useRef(null);
    const [hiddenTansition, setHideTransition] = useState(isHidden);

    useEffect(() => {
        hideAnimationRef.current = setTimeout(() => {
            setHideTransition(isHidden);
            hideAnimationRef.current = null;
        }, 400)
    }, [isHidden]);

    useEffect(() => {
        return () => {
            // Avoids calling state update after unmounting
            if (hideAnimationRef.current) {
                clearTimeout(hideAnimationRef.current);
            }
        }
    }, [])

    if (isHidden && isHidden === hiddenTansition) {
        return <div></div>
    }

    // RE-assign charge Shipping for this
    //const valorUnitario = !chargeShipping ? sales.order.total_amount : sales.order.total_amount + (sales.order.shipping_cost || 0);
    const valorUnitario = sales.valorUnitario;

    const formatTaxing = (tax: BillConceptTax[]) => {
        return (
            <div className="mq2 taxing">
                {tax.map(t => t.exento ? `${t.type.toUpperCase()} Exento` : `${t.type.toUpperCase()} ${(t.tasa || 0) * 100}%`).map((tax, i) => <p key={`key-${i}`}>{tax}</p>)}
            </div>
        )
        // return tax.map(t => `${t.type.toUpperCase()} ${t.tasa * 100}%`).join(', ');
    }

    return (
        <div className={`orderConcepts ${isHidden ? 'hidden' : (isHidden === false ? 'shown' : '')}`}>
            <Checkbox label="" value={!isHidden} onChange={toggleSelect(sales.noIdentificacion.split('.')[0])} />
            <p>
                venta <span>(id: {sales.noIdentificacion})</span>
            </p>
            <p className="precio">
                $ {numberToCurrencyString(valorUnitario)}
                {
                    /*
                    chargeShipping && (sales.order.shipping_cost || 0) ?
                        <span className="_withShipping">(Envío incluido  $ {numberToCurrencyString((sales.order.shipping_cost || 0) / 1.16)})</span>
                        : ''
                    */
                }
            </p>
            <p className="mq1">
                01010101
            </p>
            {formatTaxing(sales.impuestos)}
        </div>
    )
}

export default StampGlobalGroupingGroup;